import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import Logo from "@/components/Logo";
import { AuthAside } from "@/components/AuthAside";
import { LanguageToggle } from "@/components/LanguageToggle";
import { Loader2, ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user && user !== false) navigate(user.role === "admin" ? "/admin" : "/dashboard"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate(res.data?.role === "admin" ? "/admin" : "/dashboard");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="login-page">
      <AuthAside titleKey="auth.grindTitle" subKey="auth.grindSub" />

      <div className="flex items-center justify-center p-6 sm:p-12 relative">
        <div className="absolute top-5 right-5"><LanguageToggle /></div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo size={38} textClass="text-2xl" /></div>
          <h1 className="font-display text-3xl font-semibold mb-1">{t("auth.loginTitle")}</h1>
          <p className="text-muted-foreground mb-8 text-sm">{t("auth.loginSub")}</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-sm font-medium">{t("auth.email")}</label>
              <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="mt-1.5 w-full h-11 px-4 rounded-xl bg-card border border-border focus:outline-none focus:ring-2 focus:ring-ring" placeholder="you@example.com" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">{t("auth.password")}</label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-semibold text-primary hover:underline">{t("auth.forgot")}</Link>
              </div>
              <input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="mt-1.5 w-full h-11 px-4 rounded-xl bg-card border border-border focus:outline-none focus:ring-2 focus:ring-ring" placeholder="••••••••" />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</div>}
            <button type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{t("auth.login")} <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground text-center">
            {t("auth.newHere")} <Link to="/register" data-testid="switch-to-register" className="text-primary font-semibold hover:underline">{t("auth.createAccount")}</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
